/**
 * Rock Off Retail — "Picked For You" personalised rail renderer
 * (sections/rockoff-for-you.liquid). Client-side only, deferred, renders
 * after LCP (loaded via a deferred <script> at the bottom of the section).
 *
 * Reads window.ROAffinity (assets/rockoff-affinity.js) for the visitor's
 * followed/viewed hubs, ranks them (followed hubs first, then most-viewed
 * by count/recency — plan §E + task requirement: "followed bands' products
 * rank first"), resolves each hub handle to its display name via the
 * existing assets/hubs-data.json static asset (already shipped + fetched
 * client-side by the band-wall search — shared browser cache, no new data
 * pipeline), then pulls real products per hub via the storefront's native
 * /search/suggest.json predictive-search endpoint.
 *
 * /search/suggest.json was chosen over /collections/<handle>/products.json
 * (confirmed live 16 Aug via an authenticated staff-preview session, both
 * reachable behind the password wall): most of the 84 active hub
 * metaobjects have no dedicated Shopify Collection (only a couple, e.g.
 * Iron Maiden/Harry Potter, do — see scripts/fill-hub-collections.js), so a
 * collection-handle-per-hub lookup would silently return nothing for the
 * majority of hubs. Free-text search by hub/band name matches the site's
 * own existing pattern (nav mega-menu, band wall) and hits real inventory
 * regardless of whether a dedicated collection exists.
 *
 * FALLBACK (24 Aug, Sohail: presenting from a fresh browser with zero
 * affinity history left this rail permanently invisible): when there is
 * no affinity signal at all, or every candidate hub's search comes back
 * empty, the section no longer stays hidden — it renders best sellers
 * from the fallback collection instead (default handle "all", whose
 * default sort is best-selling), with the heading swapped to the
 * fallback heading (default "Top Sellers") so generic content is never
 * shown under the "Picked for you" label. See renderFallback() below.
 * The section only stays hidden if that fallback fetch ALSO fails or
 * returns zero cards after dedupe.
 *

 * RECOMPUTE-ON-RENDER (18 Aug bug diagnosis, see assets/rockoff-affinity.js
 * seedFromUrl() comment for the full 3-part trace): initSection() below
 * calls window.ROAffinity.get() — literally load() in rockoff-affinity.js,
 * which reads localStorage fresh, synchronously, every single call, with
 * no caching layer anywhere in this file. Because this is a classic multi-
 * page storefront (every navigation is a full document load, not client
 * routing), this function runs from scratch on every page view, so the
 * rail's ranking always reflects whatever is in localStorage AT THAT
 * MOMENT — an unfollow that happened one click ago is already visible the
 * next time this runs. The bug Sohail hit was not a stale-render problem
 * here; it traced to assets/rockoff-affinity.js's demo-seed URL handler
 * re-writing localStorage out from under a correct unfollow on every
 * repeat visit to a bookmarked demo link — fixed there.
 *
 * BLEND (18 Aug, Sohail spec): pulls from the top MAX_HUBS_TO_QUERY ranked
 * hubs (rankHubs() below — followed hubs first, most-recently-followed
 * first, then viewed-only hubs by count desc/recency desc — "weighted:
 * follows first, then view count/recency" per the brief), and INTERLEAVES
 * their results round-robin (one product from hub 1, one from hub 2, one
 * from hub 3, back to hub 1, ...) rather than exhausting the top hub
 * before moving to the next — see interleaveAndDedupe() below. Previously
 * this filled `cards` by walking each hub's product list to completion in
 * rank order, which meant a visitor with 2+ signals almost always saw a
 * wall of the #1 hub's products and nothing else (the exact opposite of a
 * "blend").
 */
(function () {
  'use strict';

  var MAX_HUBS_TO_QUERY = 3;
  // Max wait for ROConsent to settle before the first (generic) rail commit.
  var INITIAL_GRACE_MS = 300;
  var PER_HUB_FETCH_LIMIT = 6;

  function escapeForAlt(str) {
    return str == null ? '' : String(str);
  }

  function formatPrice(raw) {
    var n = parseFloat(raw);
    if (isNaN(n)) return '';
    // Rock Off Retail UK is GBP-only at this stage — same assumption the
    // rest of the theme's hand-written price markup makes.
    return '£' + n.toFixed(2);
  }

  function cdnResize(url, width) {
    if (!url) return url;
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  // Followed hubs first (most-recently-followed first), then viewed-only
  // hubs ranked by view count then recency. Deduped, capped to
  // MAX_HUBS_TO_QUERY to keep the number of search fetches small.
  function rankHubs(affinity) {
    var follows = Array.isArray(affinity.follows) ? affinity.follows.slice().reverse() : [];
    var hubs = affinity.hubs || {};
    var viewedEntries = Object.keys(hubs).map(function (handle) {
      return { handle: handle, count: hubs[handle].count || 0, last: hubs[handle].last || 0 };
    });
    viewedEntries.sort(function (a, b) {
      if (b.count !== a.count) return b.count - a.count;
      return b.last - a.last;
    });

    var ranked = [];
    var seen = {};
    follows.forEach(function (handle) {
      if (!seen[handle]) {
        ranked.push({ handle: handle, followed: true });
        seen[handle] = true;
      }
    });
    viewedEntries.forEach(function (entry) {
      if (!seen[entry.handle]) {
        ranked.push({ handle: entry.handle, followed: false });
        seen[entry.handle] = true;
      }
    });

    return ranked.slice(0, MAX_HUBS_TO_QUERY);
  }

  function fetchProductsForHub(hubName) {
    var url =
      '/search/suggest.json?q=' +
      encodeURIComponent(hubName) +
      '&resources[type]=product&resources[limit]=' +
      PER_HUB_FETCH_LIMIT +
      '&resources[options][unavailable_products]=last';
    return fetch(url)
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (data) {
        return (data && data.resources && data.resources.results && data.resources.results.products) || [];
      })
      .catch(function () {
        return [];
      });
  }

  function flagFor(product) {
    var tags = Array.isArray(product.tags) ? product.tags : [];
    var hasVault = tags.some(function (t) {
      return String(t).toLowerCase() === 'vault';
    });
    if (hasVault) return 'VAULT';
    var hasNew = tags.some(function (t) {
      return String(t).toLowerCase() === 'new';
    });
    if (hasNew) return 'NEW';
    return '';
  }

  // -------------------------------------------------------------------
  // Design-dedup (18 Aug, Sohail ruling; algorithm corrected TWICE the
  // same day — see below for both) — one product per DESIGN in this
  // rail, mirrors the server-side Liquid logic in sections/rockoff-
  // product-slider.liquid exactly (see that file's comment for the full
  // reasoning + live verification numbers); duplicated here rather than
  // shared because this is a plain script tag, not a module, and the
  // Liquid version can't run client-side anyway.
  //
  // BUGFIX 1: the original version counted total " - " segments (2 vs 3)
  // to guess whether a title carried a band prefix, taking the "middle"
  // segment as DESIGN for anything with 3 parts. That breaks the moment a
  // 2-part "DESIGN - GARMENT" title's GARMENT half itself contains " - "
  // (confirmed live: "WOLF ROSES - Smock - Tunic Casual Top" and 9 other
  // genuinely different designs all pair with the same "Smock - Tunic
  // Casual Top" garment name — the old logic took the middle segment
  // "Smock" for all ten, wrongly collapsing 10 distinct products to 1).
  //
  // BUGFIX 2: the first fix replaced segment-counting with a hardcoded
  // 13-band name whitelist (checked against segment 1) to decide "band
  // title" vs "non-band title". That regressed every non-band character
  // franchise (Harry Potter, Harley Quinn, Batman, LOTR, Deadpool, etc —
  // 20+ groups / 80+ products), because those titles ALSO follow a
  // "FRANCHISE - DESIGN - GARMENT" shape but aren't in any band roster,
  // so they always fell to the segment-1-only branch and collapsed every
  // design under one franchise-wide key — the exact class of bug this
  // whole fix exists to prevent.
  //
  // FINAL algorithm: decide by WHAT SEGMENT 2 IS, not by who segment 1
  // is. If the title has 3+ segments and segment 2 is NOT a garment word
  // (checked against GARMENT_KEYWORDS below), key on segments 1+2 joined
  // — covers real bands and character franchises identically, no name
  // roster needed. If segment 2 IS a garment word (e.g. "Smock"), key on
  // segment 1 alone — the compound-garment case. Titles with fewer than
  // 3 segments key on segment 1 (unchanged). GARMENT_KEYWORDS was built
  // empirically from this store's live productTypes plus the two other
  // confirmed compound-garment segment-2 values found scanning the full
  // catalogue ("Bag 4 Life", "Ladies Fish Tail Full Zip Hoody") — not
  // guessed. A bare "tee" was tried and rejected: false-positives as a
  // substring of real design names ("BRITISH STEEL", "SKULL TEETH").
  //
  // Verified 18 Aug against the full live 592-product catalogue (Python
  // simulation of this exact algorithm against every real title/SKU):
  // Smock family 10/10 distinct; Familiar Magic 3->1; AC/DC 9->7; Harry
  // Potter 10->6; Harley Quinn 10->7; Batman 9->8; LOTR 6->6 (incl. two
  // 4-segment titles — the rule only ever looks at segments 1-2, so
  // extra segments never matter); Deadpool 4->4; zero keys span more
  // than one genuinely different segment-2 design phrase anywhere in the
  // catalogue.
  //
  // Caveat specific to THIS file: /search/suggest.json's product resource
  // shape does not reliably include a `variants`/`sku` field (predictive-
  // search returns a slimmed-down product object — title/handle/url/
  // image/price/tags, confirmed by this endpoint's documented response
  // shape). When no SKU is present the code below safely falls through to
  // the title-based key (skuUp.length is 0, fails the >=4 check) — the
  // same branch nearly the entire live catalogue hits today anyway (every
  // current SKU, real Rock Off or -DEMO placeholder, is hyphenated — see
  // the Liquid version's comment), so this is a documented degrade, not a
  // silent miss.
  // -------------------------------------------------------------------
  var GARMENT_KEYWORDS = [
    'smock',
    'tunic',
    'hoody',
    'hoodie',
    'bag',
    'tote',
    'backpack',
    'sleepsuit',
    'robe',
    'pyjama',
    'workshirt',
    't-shirt',
    'tshirt',
    'cap',
    'beanie',
    'sock',
    'mug',
    'dress',
    'vest',
    'zip sleeve',
  ];

  function isGarmentSegment(seg) {
    var low = (seg || '').trim().toLowerCase();
    for (var i = 0; i < GARMENT_KEYWORDS.length; i++) {
      if (low.indexOf(GARMENT_KEYWORDS[i]) !== -1) return true;
    }
    return false;
  }

  // 28 Sep, one-per-design dedupe (Sohail: every product is now bridge-made
  // and carries a `design:<CODE>` tag) — this is now the PRIMARY key
  // source, checked before the SKU/title derivation below (18 Aug ruling,
  // kept as the fallback for any product whose data doesn't carry tags —
  // see the server-picks caveat at the renderServerPicks() call site).
  function designTagKey(product) {
    var tags = Array.isArray(product.tags) ? product.tags : [];
    for (var i = 0; i < tags.length; i++) {
      var t = String(tags[i]); if (t.indexOf('design:') === 0 && t.length > 7) return t;
    }
    return '';
  }

  function designDedupeKey(product) {
    var tagKey = designTagKey(product);
    if (tagKey) return 'design:' + tagKey;

    var sku = (product.variants && product.variants[0] && product.variants[0].sku) || product.sku || '';
    var skuUp = String(sku).toUpperCase();
    var isSpiralSku = false;
    if (skuUp.length >= 4 && skuUp.indexOf('-') === -1) {
      if (/^[A-Z][0-9]{3}/.test(skuUp)) {
        isSpiralSku = true;
      }
    }
    if (isSpiralSku) {
      return 'sku:' + skuUp;
    }

    var title = product.title || '';
    var parts = title.split(' - ');

    var designPart;
    if (parts.length >= 3 && !isGarmentSegment(parts[1])) {
      designPart = parts[0] + ' - ' + parts[1];
    } else {
      designPart = parts[0];
    }
    return 'title:' + designPart.trim().toLowerCase();
  }

  // Round-robin interleave across the ranked hubs' product lists (one pick
  // per hub per pass, in rank order — followed/top hub still gets first
  // pick, but doesn't dominate), applying BOTH a handle-level dedup (a
  // product literally repeated across two hubs' search results) and the
  // design-level dedup above (two different garments of the same design,
  // possibly from different hubs' results) globally across the blended
  // stream. Stops once `maxProducts` is reached or every hub's list is
  // exhausted, whichever comes first.
  function interleaveAndDedupe(results, maxProducts) {
    var cards = [];
    var seenHandles = {};
    var seenDesignKeys = {};
    var pointers = results.map(function () {
      return 0;
    });

    var progressed = true;
    while (cards.length < maxProducts && progressed) {
      progressed = false;
      for (var i = 0; i < results.length && cards.length < maxProducts; i++) {
        var list = results[i].products || [];
        while (pointers[i] < list.length) {
          var product = list[pointers[i]];
          pointers[i]++;
          if (!product || !product.handle || seenHandles[product.handle]) continue;
          var key = designDedupeKey(product);
          if (seenDesignKeys[key]) continue;
          var card = buildCard(product, results[i].hub.name);
          if (!card) continue; // no image — skip, keep pulling more candidates from this hub's list
          seenHandles[product.handle] = true;
          seenDesignKeys[key] = true;
          cards.push(card);
          progressed = true;
          break;
        }
      }
    }
    return cards;
  }

  // -------------------------------------------------------------------
  // Quick-add icon + modal (19 Aug post-demo — Sohail: "the icon is not
  // appearing in the personalised carousel", closing the deferred item
  // from the demo-day round). Mirrors snippets/rockoff-card-quickadd.liquid
  // exactly: the button/modal render as SIBLINGS of the .ro-card anchor
  // inside a .ro-card-wrap (interactive elements must not nest inside an
  // <a>), and the wrap is ALSO what restores fixed slider-item sizing —
  // the 19 Aug refactor moved flex-basis/scroll-snap from .ro-card to
  // .ro-card-wrap, and these JS rails were never updated, which is exactly
  // why their cards rendered at variable widths (Sohail's screenshot).
  // quick-add.css/quick-add.js/product-form.js are loaded by the host
  // section (sections/rockoff-for-you.liquid) — the custom elements
  // upgrade whenever those defer scripts land, before or after this
  // markup is inserted, so no load-order guard is needed.
  // SVGs inlined verbatim from assets/icon-cart.svg / icon-close.svg /
  // snippets/loading-spinner.liquid (plain script tag — no
  // inline_asset_content equivalent client-side).
  // -------------------------------------------------------------------
  var CART_ICON_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-cart" viewBox="0 0 40 40"><path fill="currentColor" fill-rule="evenodd" d="M20.5 6.5a4.75 4.75 0 0 0-4.75 4.75v.56h-3.16l-.77 11.6a5 5 0 0 0 4.99 5.34h7.38a5 5 0 0 0 4.99-5.33l-.77-11.6h-3.16v-.57A4.75 4.75 0 0 0 20.5 6.5m3.75 5.31v-.56a3.75 3.75 0 1 0-7.5 0v.56zm-7.5 1h7.5v.56a3.75 3.75 0 1 1-7.5 0zm-1 0v.56a4.75 4.75 0 1 0 9.5 0v-.56h2.22l.71 10.67a4 4 0 0 1-3.99 4.27h-7.38a4 4 0 0 1-4-4.27l.72-10.67z"/></svg>';
  var CLOSE_ICON_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-close" viewBox="0 0 18 17"><path fill="currentColor" d="M.865 15.978a.5.5 0 0 0 .707.707l7.433-7.431 7.579 7.282a.501.501 0 0 0 .846-.37.5.5 0 0 0-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 1 0-.707-.708L8.991 7.853 1.413.573a.5.5 0 1 0-.693.72l7.563 7.268z"/></svg>';
  var SPINNER_SVG =
    '<div class="loading__spinner hidden"><svg xmlns="http://www.w3.org/2000/svg" class="spinner" viewBox="0 0 66 66"><circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle></svg></div>';

  function escapeAttr(str) {
    return String(str == null ? '' : str).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
  }

  var quickaddLabels = { add: 'Choose options', close: 'Close' };

  // Locale-aware URL root (independent checker finding, 19 Aug): the
  // quick-add modal fetches this URL and injects the returned product
  // markup — without the locale root a /de/ or /fr/ visitor gets an
  // English modal. Same prefix applied to the card hrefs.
  function localeRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function localeUrl(path) {
    return localeRoot() + String(path || '').replace(/^\//, '');
  }

  function buildQuickAdd(productId, productUrl) {
    var div = document.createElement('div');
    div.className = 'ro-card-quickadd';
    // Rail-specific id prefix ("fy") so the same product appearing in BOTH
    // homepage JS rails never mints duplicate DOM ids (checker finding);
    // Dawn's lookups are prefix matches (id^="ModalClose-"), so any suffix
    // scheme works. productId is numeric from /search/suggest.json (handle
    // fallback is a Shopify handle: [a-z0-9-]), escaped anyway.
    var id = 'QuickAdd-fy' + escapeAttr(productId);
    // Built inside an inert <template> ON PURPOSE (live-debugged 19 Aug):
    // a plain div.innerHTML synchronously CONSTRUCTS already-defined custom
    // elements mid-parse — Dawn's ModalDialog constructor then runs before
    // the modal's children exist and crashes on a null close-button lookup
    // ("Cannot read properties of null (reading 'addEventListener')",
    // stack: new ModalDialog <- quick-add.js <- buildQuickAdd). Template
    // content is inert, so the elements only upgrade when the finished
    // card is appended to the document — children complete, constructor
    // happy. The close button MUST carry an id starting "ModalClose-"
    // (second half of the same crash): ModalDialog's constructor binds its
    // hide() to exactly that selector.
    var tpl = document.createElement('template');
    tpl.innerHTML =
      '<modal-opener data-modal="#' + id + '">' +
      '<button type="button" class="ro-card-quickadd__btn" aria-haspopup="dialog" title="' + escapeAttr(quickaddLabels.add) + '" data-product-url="' + escapeAttr(productUrl) + '">' +
      '<span class="visually-hidden">' + escapeAttr(quickaddLabels.add) + '</span>' +
      '<span class="ro-card-quickadd__glyph" aria-hidden="true">' + CART_ICON_SVG + '</span>' +
      SPINNER_SVG +
      '</button>' +
      '</modal-opener>' +
      '<quick-add-modal id="' + id + '" class="quick-add-modal">' +
      '<div role="dialog" aria-label="' + escapeAttr(quickaddLabels.add) + '" aria-modal="true" class="quick-add-modal__content global-settings-popup" tabindex="-1">' +
      '<button id="ModalClose-fy' + escapeAttr(productId) + '" type="button" class="quick-add-modal__toggle" aria-label="' + escapeAttr(quickaddLabels.close) + '">' + CLOSE_ICON_SVG + '</button>' +
      '<div id="QuickAddInfo-fy' + escapeAttr(productId) + '" class="quick-add-modal__content-info"></div>' +
      '</div>' +
      '</quick-add-modal>';
    div.appendChild(tpl.content);
    return div;
  }

  // 24 Sep, Sohail item 6: skip the WHOLE card when there is no image,
  // rather than only omitting the <img> tag — previously a product with no
  // image still rendered its title/price with an empty well (looked like
  // stray alt text with no picture). Every call site below must check for
  // this null and skip it (never append it, never count it toward a rail's
  // shown total) — see interleaveAndDedupe()/dedupeAndBuildCards()/
  // renderServerPicks() for the matching filter + backfill-from-remaining-
  // candidates logic.
  function buildCard(product, bandName) {
    if (!product || !product.image) return null;

    var a = document.createElement('a');
    a.className = 'ro-card';
    a.href = localeUrl('/products/' + product.handle);

    var well = document.createElement('span');
    well.className = 'ro-card__well';

    var flag = flagFor(product);
    if (flag) {
      var flagEl = document.createElement('span');
      flagEl.className = 'ro-card__flag';
      flagEl.textContent = flag;
      well.appendChild(flagEl);
    }

    var img = document.createElement('img');
    img.className = 'ro-card__img ro-card__img--primary';
    img.src = cdnResize(product.image, 500);
    img.alt = escapeForAlt(product.title);
    img.loading = 'lazy';
    well.appendChild(img);

    a.appendChild(well);

    var title = document.createElement('h4');
    title.className = 'ro-card__title';
    title.textContent = product.title || '';
    a.appendChild(title);

    if (bandName) {
      var band = document.createElement('span');
      band.className = 'ro-card__band';
      band.textContent = bandName;
      a.appendChild(band);
    }

    var priceEl = document.createElement('div');
    priceEl.className = 'ro-card__price';
    priceEl.textContent = formatPrice(product.price);
    a.appendChild(priceEl);

    // .ro-card-wrap carries the slider-item sizing (flex-basis/scroll-snap
    // — section-rockoff-product-slider.css) and is the positioning context
    // for the quick-add icon overlay. See the buildQuickAdd comment above.
    var wrap = document.createElement('div');
    wrap.className = 'ro-card-wrap';
    wrap.appendChild(a);
    wrap.appendChild(buildQuickAdd(product.id || product.handle, localeUrl('/products/' + product.handle)));
    return wrap;
  }

  // -------------------------------------------------------------------
  // No-affinity fallback (24 Aug, Sohail: presenting from a fresh browser
  // on another computer — zero affinity signal — left this whole rail
  // invisible). The Just Dropped rail further down the homepage already
  // covers "new arrivals" from the just-dropped collection, so this
  // fallback deliberately uses BEST SELLERS instead, never the same
  // collection: /collections/<handle>/products.json's default sort is
  // best-selling (Shopify convention, unaffected by manual/custom sort
  // overrides only on genuinely manual-sorted collections — "all" is not
  // manually sorted on this store), so no `?sort_by=` param is needed.
  // Triggers: (a) no affinity signal at all (ranked.length === 0), or
  // (b) the personalised pipeline resolves but yields zero cards (no hub
  // names matched hubs-data.json, or every candidate hub's search came
  // back empty/fully duplicate), or (c) the personalised fetch chain
  // itself throws (e.g. hubs-data.json fails to load/parse). Heading is
  // swapped to the fallback heading so generic content is never presented
  // under the "Picked for you" label, and the See All link is repointed
  // to the fallback collection with the base label only (no hub name —
  // there's no personalisation to attribute it to). Reuses the exact same
  // buildCard/quick-add markup and the same design-level dedupe as the
  // personalised path. Fails safe: if this fetch also errors or returns
  // zero cards after dedupe, the section stays hidden, same as today.
  // -------------------------------------------------------------------
  // 17 Oct 2026 (Gary: lead with pop culture and Spiral, not bands): the
  // fallback setting may list several handles, comma-separated
  // ("all-characters,all-spiral"). Each collection is fetched and the lists
  // are interleaved one product at a time, so the rail mixes them. "all" is
  // title-sorted on this store (AC/DC filled the rail), so the named
  // collections, which the best-seller app sorts by 2-week sales, are used.
  function fallbackHandles(handle) {
    var list = String(handle || '').split(',').map(function (h) {
      return h.trim();
    }).filter(Boolean);
    return list.length ? list : ['all'];
  }

  function fetchOneCollection(handle) {
    var url = localeUrl('/collections/' + handle + '/products.json?limit=12');
    return fetch(url)
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (data) {
        return (data && Array.isArray(data.products)) ? data.products : [];
      })
      .catch(function () {
        return [];
      });
  }

  function fetchFallbackProducts(handle) {
    return Promise.all(fallbackHandles(handle).map(fetchOneCollection)).then(function (lists) {
      var mixed = [];
      var longest = Math.max.apply(null, lists.map(function (l) { return l.length; }).concat([0]));
      for (var i = 0; i < longest; i++) {
        for (var j = 0; j < lists.length; j++) {
          if (lists[j][i]) mixed.push(lists[j][i]);
        }
      }
      return mixed;
    });
  }

  // Maps the /products.json product shape onto the {image, price, handle,
  // tags, id, variants} shape buildCard()/designDedupeKey() expect — the
  // same fields /search/suggest.json's product resource already carries,
  // just under different keys (images[0].src vs image, variants[0].price
  // vs price).
  function mapCollectionProduct(product) {
    var variant = (product.variants && product.variants[0]) || {};
    return {
      id: product.id,
      handle: product.handle,
      title: product.title,
      tags: Array.isArray(product.tags) ? product.tags : [],
      image: product.images && product.images[0] && product.images[0].src,
      price: variant.price,
      variants: product.variants,
    };
  }

  // Same handle + design-level dedupe as interleaveAndDedupe() above, just
  // over a single flat list rather than several interleaved hub lists.
  function dedupeAndBuildCards(products, maxProducts) {
    var cards = [];
    var seenHandles = {};
    var seenDesignKeys = {};
    for (var i = 0; i < products.length && cards.length < maxProducts; i++) {
      var product = products[i];
      if (!product || !product.handle || seenHandles[product.handle]) continue;
      var key = designDedupeKey(product);
      if (seenDesignKeys[key]) continue;
      var card = buildCard(product);
      if (!card) continue; // no image — skip, continue scanning remaining candidates
      seenHandles[product.handle] = true;
      seenDesignKeys[key] = true;
      cards.push(card);
    }
    return cards;
  }

  // -------------------------------------------------------------------
  // Render-generation guard (fix round 2, 29 Sep 2026). Every start() of the
  // pipeline is a "run" {id, epoch}. A run may only COMMIT (write the rail)
  // if no newer run has already committed (id > state.lastCommit), and a
  // result built from PASSIVE history may only commit while
  // ROConsent.allowed('personalisation') is still true and no consent
  // withdrawal happened since the run started (epoch unchanged). Generic
  // (non-passive) results never wait for or depend on consent. A stale async
  // result is therefore simply discarded.
  // -------------------------------------------------------------------
  function isStale(opts) {
    var st = opts && opts.state;
    var run = opts && opts.run;
    return !!(st && run && run.id <= st.lastCommit);
  }

  function canCommit(opts, passive) {
    var st = opts && opts.state;
    var run = opts && opts.run;
    if (!st || !run) return !passive || serverConsented();
    if (run.id <= st.lastCommit) return false;
    if (passive && (!serverConsented() || run.epoch !== st.epoch)) return false;
    return true;
  }

  // Empties the rail and restores the static shell (heading, See-all link).
  function clearRail(opts) {
    while (opts.productsEl.firstChild) opts.productsEl.removeChild(opts.productsEl.firstChild);
    if (opts.headingEl && opts.originalHeading != null) opts.headingEl.textContent = opts.originalHeading;
    if (opts.seeAllEl) {
      opts.seeAllEl.hidden = true;
      if (opts.originalSeeAllHref != null) opts.seeAllEl.href = opts.originalSeeAllHref;
      opts.seeAllEl.textContent = opts.originalSeeAllText != null ? opts.originalSeeAllText : '';
    }
    opts.section.hidden = true;
    opts.section.removeAttribute('data-ro-picks-source');
  }

  // Claims the rail for this run (clears whatever was there) - synchronous
  // with the appends that follow, so there is no empty paint in between.
  function beginCommit(opts, passive) {
    if (opts.state && opts.run) {
      opts.state.lastCommit = opts.run.id;
      opts.state.railPassive = !!passive;
    }
    clearRail(opts);
  }

  function renderFallback(opts) {
    fetchFallbackProducts(opts.fallbackHandle)
      .then(function (rawProducts) {
        if (!canCommit(opts, false)) return; // superseded by a newer run
        var mapped = rawProducts.map(mapCollectionProduct);
        var cards = dedupeAndBuildCards(mapped, opts.maxProducts);
        if (!cards.length) return; // fallback fetch also came back empty — stay hidden, as today

        beginCommit(opts, false);
        cards.forEach(function (card) {
          opts.productsEl.appendChild(card);
        });

        // Heading swap — never leave "Picked for you" over generic
        // best-sellers content (task requirement).
        if (opts.headingEl) opts.headingEl.textContent = opts.fallbackHeading;

        if (opts.seeAllEl) {
          // Base label only, no hub name — this isn't attributable to any
          // one hub.
          // A mixed rail (several handles) has no single collection that
          // holds every card, so it shows no See all link (Codex r1, 17 Oct).
          var fbHandles = fallbackHandles(opts.fallbackHandle);
          opts.seeAllEl.href = localeUrl('/collections/' + fbHandles[0]);
          opts.seeAllEl.textContent = opts.seeAllLabel;
          opts.seeAllEl.hidden = fbHandles.length > 1;
        }

        opts.section.hidden = false;
      })
      .catch(function () {
        // Fallback fetch itself failed — stay hidden, as today.
      });
  }


  // -------------------------------------------------------------------
  // Personalisation v2 server picks (15 Sep 2026, build prompt step 5).
  // Behind the section setting `server_picks` (default OFF). When on and
  // consented: ask the worker (direct URL from rockoff-affinity.js
  // workerUrl, or /apps/ro-picks/picks when `picks_via_proxy` is on and
  // the app proxy exists), render its 3-tier picks; on ANY error, empty
  // response, or no consent, fall back to the existing browser-side path
  // below, unchanged. Identity sent: __kla_id, ro_vid, logged-in customer
  // id (meta from layout/theme.liquid; read-only on the worker's unsigned
  // route). Local affinity is sent too so a first visit still gets tier 1.
  // -------------------------------------------------------------------
  function serverConsented() {
    try {
      return !!(window.ROConsent && typeof window.ROConsent.allowed === 'function' && window.ROConsent.allowed('personalisation') === true);
    } catch (err) {
      return false;
    }
  }

  function readCookieValue(name) {
    try {
      var match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
      return match ? decodeURIComponent(match[1]) : null;
    } catch (err) {
      return null;
    }
  }

  function fetchServerPicks(affinity, limit, viaProxy) {
    if (!serverConsented()) return Promise.resolve(null);
    var workerUrl = window.ROAffinity && window.ROAffinity.workerUrl;
    if (!workerUrl && !viaProxy) return Promise.resolve(null);
    var compact = { v: 1, f: (affinity && affinity.follows) || [], h: {} };
    if (affinity && affinity.hubs) {
      Object.keys(affinity.hubs).forEach(function (k) {
        compact.h[k] = [affinity.hubs[k].count, affinity.hubs[k].last];
      });
    }
    var params = new URLSearchParams({ consent: '1', limit: String(limit || 8), affinity: JSON.stringify(compact) });
    var kla = readCookieValue('__kla_id');
    if (kla) params.set('kla_id', kla);
    var vid = window.ROAffinity && typeof window.ROAffinity.getVid === 'function' ? window.ROAffinity.getVid() : readCookieValue('ro_vid');
    if (vid) params.set('vid', vid);
    var custMeta = document.querySelector('meta[name="ro-customer-id"]');
    if (custMeta && custMeta.getAttribute('content')) params.set('customer_id', custMeta.getAttribute('content'));
    // One-shot reset after a demo_affinity seed (set by rockoff-affinity.js):
    // the server replaces its stored record with this client record.
    var resetPending = false;
    try {
      resetPending = window.localStorage.getItem('ro_affinity_reset_v1') === '1';
    } catch (err) {
      resetPending = false;
    }
    if (resetPending) params.set('reset', '1');
    var qs = params.toString();
    function clearReset(json) {
      if (resetPending && json && json.ok) {
        try {
          window.localStorage.removeItem('ro_affinity_reset_v1');
        } catch (err) {
          /* ignore */
        }
      }
      return json;
    }

    function asJson(r) {
      return r && r.ok ? r.json() : null;
    }
    function direct() {
      if (!workerUrl) return Promise.resolve(null);
      // Consent may have been withdrawn while the proxy call was in flight:
      // the fallback must not send the history (consent=1) to the worker.
      if (!serverConsented()) return Promise.resolve(null);
      return fetch(workerUrl + '/picks?' + qs, { mode: 'cors', credentials: 'omit' })
        .then(asJson)
        .then(clearReset)
        .catch(function () {
          return null;
        });
    }
    if (!viaProxy) return direct();
    if (!serverConsented()) return Promise.resolve(null); // re-check right before the proxy call
    return fetch('/apps/ro-picks/picks?' + qs)
      .then(asJson)
      .then(function (json) {
        return json && json.ok ? clearReset(json) : direct();
      })
      .catch(direct);
  }

  // Renders the worker's response through the same buildCard/dedupe path
  // as the browser pipeline. Returns a promise of true when cards landed.
  function renderServerPicks(resp, hubsJsonUrl, opts) {
    // 17 Oct 2026: tier 3 is the worker's store-wide top sellers, which are
    // band-led. Return false so the caller drops to clientPath(), which ends
    // in the theme's own pop culture + Spiral fallback mix.
    if (resp && resp.tier === 3) return Promise.resolve(false);
    var namesP = hubsJsonUrl
      ? fetch(hubsJsonUrl)
          .then(function (r) {
            return r.ok ? r.json() : [];
          })
          .catch(function () {
            return [];
          })
      : Promise.resolve([]);
    return namesP.then(function (list) {
      // Server picks are built from passive history: re-check consent (and
      // that this run is still current) at the moment of rendering
      // (false -> caller falls back to clientPath(), which drops to generic).
      if (!canCommit(opts, true)) return false;
      var nameMap = {};
      (Array.isArray(list) ? list : []).forEach(function (hub) {
        if (hub && hub.url) nameMap[hub.url.replace('/pages/hubs/', '')] = hub.name;
      });
      var cards = [];
      var seenHandles = {};
      var seenDesignKeys = {};
      resp.picks.forEach(function (p) {
        if (!p || !p.handle || cards.length >= opts.maxProducts || seenHandles[p.handle]) return;
        // 28 Sep: the personalisation worker's picks payload carries no tags
        // (personalisation-worker/lib/ranking.js picks.push: id, handle,
        // title, image, price, score), so Picked For You server picks are NOT
        // design-tag deduped; they fall to the SKU/title key. Follow-up:
        // add tags to the worker payload. The passthrough below is kept so
        // that change needs no theme edit.
        var product = { id: p.id, handle: p.handle, title: p.title || '', image: p.image, price: p.price, tags: Array.isArray(p.tags) ? p.tags : [], variants: [] };
        var key = designDedupeKey(product);
        if (seenDesignKeys[key]) return;
        var card = buildCard(product, p.hub ? nameMap[p.hub] || '' : '');
        if (!card) return; // no image — skip this pick
        seenHandles[p.handle] = true;
        seenDesignKeys[key] = true;
        cards.push(card);
      });
      if (!cards.length) return false;
      beginCommit(opts, true);
      cards.forEach(function (card) {
        opts.productsEl.appendChild(card);
      });
      if (resp.tier === 3) {
        // Store top sellers: same honesty rule as renderFallback().
        if (opts.headingEl) opts.headingEl.textContent = opts.fallbackHeading;
        if (opts.seeAllEl) {
          // A mixed rail (several handles) has no single collection that
          // holds every card, so it shows no See all link (Codex r1, 17 Oct).
          var fbHandles = fallbackHandles(opts.fallbackHandle);
          opts.seeAllEl.href = localeUrl('/collections/' + fbHandles[0]);
          opts.seeAllEl.textContent = opts.seeAllLabel;
          opts.seeAllEl.hidden = fbHandles.length > 1;
        }
      } else if (opts.seeAllEl && resp.hubs && resp.hubs[0]) {
        var top = resp.hubs[0];
        opts.seeAllEl.href = localeUrl('/pages/hubs/' + top);
        opts.seeAllEl.textContent = opts.seeAllLabel + (nameMap[top] ? ' ' + nameMap[top] : '');
        opts.seeAllEl.hidden = false;
      }
      opts.section.hidden = false;
      opts.section.setAttribute('data-ro-picks-source', 'server-tier-' + resp.tier + (resp.recognised ? '-recognised' : ''));
      return true;
    });
  }

  function initSection(section) {
    // BUGFIX (17 Aug Sunday sweep, item 7): sections/rockoff-for-you.liquid
    // renders a static placeholder (4 cards + note) and leaves the section
    // un-hidden ONLY inside request.design_mode, so the theme editor can
    // show/position it. That placeholder must never be touched by the real-
    // visitor fetch/rank/render pipeline below — Shopify.designMode is the
    // JS-side twin of the Liquid check (both true in exactly the same
    // editor context), so bail out here before any of the real logic
    // (which would otherwise fetch products, overwrite the placeholder
    // cards, or flip the section back to [hidden]) ever runs.
    if (section.getAttribute('data-ro-design-mode') === 'true' || window.Shopify?.designMode) return;

    var hubsJsonUrl = section.getAttribute('data-ro-hubs-json-url');
    var maxProducts = parseInt(section.getAttribute('data-ro-products-to-show'), 10) || 8;
    var seeAllLabel = section.getAttribute('data-ro-see-all-label') || 'See all';
    // Locale-correct quick-add labels, passed down by the section's Liquid
    // (data-ro-quickadd-label / data-ro-close-label) so DE/FR resolve.
    quickaddLabels.add = section.getAttribute('data-ro-quickadd-label') || quickaddLabels.add;
    quickaddLabels.close = section.getAttribute('data-ro-close-label') || quickaddLabels.close;
    var productsEl = section.querySelector('[data-ro-for-you-products]');
    var seeAllEl = section.querySelector('[data-ro-for-you-seeall]');
    var headingEl = section.querySelector('[data-ro-heading-el]');
    var fallbackHeading = section.getAttribute('data-ro-fallback-heading') || 'Top Sellers';
    var fallbackHandle = section.getAttribute('data-ro-fallback-collection') || 'all';
    if (!productsEl) return;

    var fallbackOpts = {
      section: section,
      productsEl: productsEl,
      seeAllEl: seeAllEl,
      headingEl: headingEl,
      fallbackHeading: fallbackHeading,
      fallbackHandle: fallbackHandle,
      maxProducts: maxProducts,
      seeAllLabel: seeAllLabel,
      originalHeading: headingEl ? headingEl.textContent : null,
      originalSeeAllHref: seeAllEl ? seeAllEl.getAttribute('href') : null,
      originalSeeAllText: seeAllEl ? seeAllEl.textContent : null,
      state: { id: 0, epoch: 0, lastCommit: 0, railPassive: false },
      run: null,
    };

    if (!window.ROAffinity) {
      renderFallback(fallbackOpts);
      return;
    }

    var serverPicksOn = section.getAttribute('data-ro-server-picks') === 'true';
    var viaProxy = section.getAttribute('data-ro-picks-via-proxy') === 'true';
    var state = fallbackOpts.state;

    // Consent gate (fix rounds 1+2, 29 Sep 2026). Passive history (hub view
    // counts, viewed products) is only ever read/ranked/sent while
    // ROConsent.allowed('personalisation') is true AT THE MOMENT OF USE -
    // re-checked immediately before EVERY personalised network request (each
    // hub search, the proxy call, the direct-worker fallback) and before
    // rendering. Explicit state the visitor created (followed bands) is
    // functional storage and is used without consent. Generic best-sellers
    // never wait for consent.
    function currentRanking() {
      var record = window.ROAffinity.get() || {};
      var consented = serverConsented();
      var view = {
        follows: Array.isArray(record.follows) ? record.follows : [],
        hubs: consented && record.hubs && typeof record.hubs === 'object' ? record.hubs : {},
      };
      var ranked = rankHubs(view);
      var usedPassive = ranked.some(function (r) {
        return !r.followed;
      });
      return { ranked: ranked, usedPassive: usedPassive };
    }

    // One pass of the pipeline. Called immediately on init (consent unknown
    // -> followed bands or generic best sellers), again when consent turns
    // on (upgrade), and again when it is withdrawn (back to generic).
    function start() {
      var run = { id: ++state.id, epoch: state.epoch };
      var opts = {};
      for (var k in fallbackOpts) {
        if (Object.prototype.hasOwnProperty.call(fallbackOpts, k)) opts[k] = fallbackOpts[k];
      }
      opts.run = run;

      // Personalisation v2 (setting `server_picks`, default OFF): try the
      // worker first; every failure path lands in clientPath() below, which
      // is the pre-15-Sep pipeline verbatim (plus the consent gate above).
      if (serverPicksOn && serverConsented()) {
        fetchServerPicks(window.ROAffinity.get(), maxProducts, viaProxy)
          .then(function (resp) {
            if (isStale(opts)) return;
            if (!resp || !resp.ok || !resp.consent || !Array.isArray(resp.picks) || !resp.picks.length) {
              clientPath();
              return;
            }
            // Re-check right before rendering the async result.
            if (!canCommit(opts, true)) {
              clientPath();
              return;
            }
            return renderServerPicks(resp, hubsJsonUrl, opts).then(function (rendered) {
              if (!rendered) clientPath();
            });
          })
          .catch(function () {
            clientPath();
          });
      } else {
        clientPath();
      }

      function clientPath() {
        if (isStale(opts)) return;
        var view = currentRanking();
        var ranked = view.ranked;
        if (!ranked.length) {
          // No affinity signal at all — skip the personalised fetches
          // entirely and go straight to best sellers.
          renderFallback(opts);
          return;
        }

        if (!hubsJsonUrl) {
          renderFallback(opts);
          return;
        }

        fetch(hubsJsonUrl)
          .then(function (r) {
            return r.ok ? r.json() : [];
          })
          .then(function (list) {
            var nameMap = {};
            (Array.isArray(list) ? list : []).forEach(function (hub) {
              if (!hub || !hub.url) return;
              nameMap[hub.url.replace('/pages/hubs/', '')] = hub.name;
            });

            var withNames = ranked
              .map(function (r) {
                return { handle: r.handle, followed: r.followed, name: nameMap[r.handle] };
              })
              .filter(function (r) {
                return !!r.name;
              });

            if (!withNames.length) return null;

            // Consent withdrawn (or run superseded) while hubs.json was
            // loading: do not start ANY follow-on search built from passive
            // history.
            if (isStale(opts)) return 'stale';
            if (view.usedPassive && !canCommit(opts, true)) return null;

            return Promise.all(
              withNames.map(function (hub) {
                // Per-request re-check: a hub that is not an explicit follow
                // came from passive history and needs consent at this very
                // moment.
                if (!hub.followed && !serverConsented()) return Promise.resolve({ hub: hub, products: [] });
                return fetchProductsForHub(hub.name).then(function (products) {
                  return { hub: hub, products: products };
                });
              })
            );
          })
          .then(function (results) {
            if (results === 'stale' || isStale(opts)) return;
            // Re-check consent right before rendering: results built from
            // passive history must not be shown if personalisation is no
            // longer allowed.
            if (results && view.usedPassive && !canCommit(opts, true)) results = null;
            if (!results) {
              renderFallback(opts); // no candidate hub resolved a name / consent gone — generic
              return;
            }

            // Blend: interleaved round-robin across the ranked hubs (top/
            // followed hub first each round, but not exhausted before the
            // others get a turn), deduped by handle AND by design — see
            // interleaveAndDedupe() above.
            var cards = interleaveAndDedupe(results, maxProducts);

            if (!cards.length) {
              renderFallback(opts); // every candidate hub came back empty (or fully duplicate)
              return;
            }

            beginCommit(opts, view.usedPassive);
            cards.forEach(function (card) {
              productsEl.appendChild(card);
            });

            if (seeAllEl) {
              // 19 Aug (Sohail): "See All" used to search only the TOP hub's
              // name while LOOKING like it covered the whole blended rail.
              // A true "all my picks" destination needs USF's band-facet
              // multi-select URL — tried live and not derivable safely
              // (free-text "A OR B" is matched literally by USF: 1 fuzzy
              // result; facet URL params aren't documented and the runtime
              // serializer isn't inspectable) — so be HONEST instead: link to
              // the top followed hub's own hub page and SAY which hub it is
              // ("See all Iron Maiden"), locale-prefixed. Revisit once the
              // Sobooster facet-URL syntax is confirmed in their admin.
              var top = results[0].hub;
              seeAllEl.href = localeUrl('/pages/hubs/' + top.handle);
              seeAllEl.textContent = seeAllLabel + ' ' + top.name;
              seeAllEl.hidden = false;
            }

            section.hidden = false;
          })
          .catch(function () {
            // Any failure in the personalised chain — fall back to best
            // sellers rather than leaving the rail invisible.
            renderFallback(opts);
          });
      } // end clientPath()
    } // end start()

    // First commit. If ROConsent exists and has NOT settled yet, defer the
    // first (generic) commit until onReady fires or a short grace timer
    // (INITIAL_GRACE_MS), whichever comes first - so a consented returning
    // visitor does not see "Top Sellers" flash and then swap to "Picked for
    // you" (layout shift + an extra products.json fetch on every load).
    // ROConsent absent, or already settled (loaded OR definitively failed):
    // commit immediately. After the first commit, later consent changes still
    // upgrade / downgrade the rail through onReady / onChange below.
    // startInitial() is one-shot: grace timer, onReady and onChange can never
    // both commit the initial run.
    var lastConsent = false;
    var initialDone = false;
    var graceTimer = null;
    var settledSync = false;
    var consent = window.ROConsent;

    function startInitial() {
      if (initialDone) return false;
      initialDone = true;
      if (graceTimer !== null) {
        clearTimeout(graceTimer);
        graceTimer = null;
      }
      start();
      lastConsent = serverConsented();
      return true;
    }

    var registering = true;
    if (consent && typeof consent.onReady === 'function') {
      consent.onReady(function () {
        if (registering) {
          settledSync = true; // already settled: commit immediately below
          return;
        }
        if (startInitial()) return; // first commit happens now, sees settled state
        var c = serverConsented();
        if (c && !lastConsent) {
          lastConsent = true;
          start(); // grace already committed generic: upgrade to personalised
        }
      });
    }
    registering = false;

    if (consent && typeof consent.onReady === 'function' && !settledSync) {
      graceTimer = setTimeout(function () {
        graceTimer = null;
        startInitial();
      }, INITIAL_GRACE_MS);
    } else {
      startInitial();
    }

    // Live changes: acceptance upgrades the rail; withdrawal replaces any
    // personalised cards with generic and invalidates in-flight passive runs.
    if (consent && typeof consent.onChange === 'function') {
      consent.onChange(function () {
        startInitial(); // no-op unless the first commit is still deferred
        var c = serverConsented();
        if (c === lastConsent) return; // some other category changed
        lastConsent = c;
        if (c) {
          start();
        } else {
          state.epoch++; // in-flight passive results now fail canCommit()
          if (state.railPassive) clearRail(fallbackOpts);
          start(); // generic (or followed bands only)
        }
      });
    }
  }

  function init() {
    var sections = document.querySelectorAll('[data-ro-for-you]');
    sections.forEach(function (section) {
      initSection(section);
    });
  }

  // The rail no longer waits for the consent API: initSection() renders the
  // generic (or followed-bands) rail immediately and upgrades / downgrades on
  // ROConsent.onReady / onChange (see initSection).
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Test-only export hook (24 Sep, item 6 node harness) — no-op in the
  // browser, where `module` is undefined; lets a plain `node --test`/CommonJS
  // harness exercise the no-image-skip logic without a real DOM or fetch.
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { buildCard: buildCard, interleaveAndDedupe: interleaveAndDedupe, dedupeAndBuildCards: dedupeAndBuildCards, designDedupeKey: designDedupeKey };
  }
})();
